import { useState, useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { Button, Input, message, Result } from 'antd'
import { SkeletonUpgrade } from '../features/skeleton'
import {
  CheckOutlined,
  QuestionCircleOutlined,
  ArrowRightOutlined,
  CrownOutlined,
  RocketOutlined,
} from '@ant-design/icons'
import { useSearchParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { PaymentService } from '../services/payments/paymentService'
import { ProfileService, type TierComparisonData } from '../services/profile/profileService'
import { useAuthContext } from '../context/auth'
import { useFeatureAccessContext } from '../context/featureAccess'
import { analytics } from '../utils/analytics'
import {
  COLORS,
  BORDER_RADIUS,
  SPACING,
  TRANSITIONS,
  TYPOGRAPHY,
  FONT_WEIGHT,
} from '../styles/theme'

const PageContainer = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: ${SPACING.xxl} ${SPACING.lg};
  min-height: 100dvh;
`

const Header = styled.div`
  text-align: center;
  margin-bottom: ${SPACING.xxl};
`

const Title = styled.h1`
  font-size: ${TYPOGRAPHY.h2.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  margin: 0 0 ${SPACING.md} 0;
  color: var(--color-text-primary);
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.sm};
`

const Subtitle = styled.p`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
`

const TrialHighlight = styled.span`
  color: ${COLORS.primary};
  font-weight: ${FONT_WEIGHT.semibold};
`

// Comparison Table
const ComparisonTable = styled.div`
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.xl};
  overflow: hidden;
  background: var(--color-bg-secondary);
  margin-bottom: ${SPACING.xl};
`

const ComparisonHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  background: var(--color-bg-elevated);
  border-bottom: 1px solid var(--color-border);
`

const ComparisonHeaderCell = styled.div<{ $highlight?: boolean }>`
  padding: ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${(props) => (props.$highlight ? COLORS.primary : 'var(--color-text-primary)')};
  text-align: center;
  &:first-child {
    text-align: left;
  }
`

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`

const ComparisonCell = styled.div<{ $highlight?: boolean }>`
  padding: ${SPACING.sm} ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.$highlight ? 'rgba(110, 83, 249, 0.05)' : 'transparent')};
  &:first-child {
    justify-content: flex-start;
  }
  .anticon-check {
    color: ${COLORS.primary};
    font-size: ${TYPOGRAPHY.body.fontSize};
  }
`

const PriceRow = styled(ComparisonRow)`
  background: var(--color-bg-elevated);
`

const PriceCell = styled(ComparisonCell)`
  flex-direction: column;
  padding: ${SPACING.lg};
  gap: 4px;
  &:first-child {
    align-items: flex-start;
  }
`

const PriceAmount = styled.span`
  font-size: ${TYPOGRAPHY.h2.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  color: var(--color-text-primary);
  letter-spacing: -1px;
`

const PricePeriod = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
`

const FeatureWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const TooltipIcon = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  transition: color ${TRANSITIONS.fast};
  width: 16px;
  height: 16px;
  &:hover {
    color: ${COLORS.primary};
  }
  .anticon {
    font-size: ${TYPOGRAPHY.caption.fontSize};
  }
`

const TooltipContent = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.md};
  padding: 10px 12px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  line-height: 1.5;
  width: 220px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
  transition:
    opacity 0.2s,
    visibility 0.2s;
  z-index: 100;
  pointer-events: none;
`

function Tooltip({ content }: { content: string }) {
  const [visible, setVisible] = useState(false)
  return (
    <TooltipWrapper onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      <TooltipIcon type="button" aria-label="More info">
        <QuestionCircleOutlined />
      </TooltipIcon>
      <TooltipContent $visible={visible}>{content}</TooltipContent>
    </TooltipWrapper>
  )
}

// CTA Section
const CTASection = styled.div`
  text-align: center;
  margin-top: ${SPACING.xl};
`

const UpgradeButton = styled(Button)`
  height: 52px;
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  padding: 0 32px;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, #9b7dfc 100%);
  border: none;
  box-shadow: 0 4px 12px ${COLORS.primaryShadow};

  &:hover {
    background: linear-gradient(135deg, #5a43d9 0%, #8a6aec 100%);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px ${COLORS.primaryShadow};
  }
`

const ContinueFreeLink = styled.button`
  display: block;
  margin: ${SPACING.md} auto 0;
  background: none;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  cursor: pointer;
  padding: ${SPACING.sm} ${SPACING.lg};
  transition:
    border-color ${TRANSITIONS.fast},
    color ${TRANSITIONS.fast};

  &:hover {
    border-color: var(--color-text-secondary);
    color: var(--color-text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const WelcomeIcon = styled.div`
  font-size: ${TYPOGRAPHY.h1.fontSize};
  margin-bottom: ${SPACING.md};
`

const PromoCodeSection = styled.div`
  margin-bottom: ${SPACING.lg};
  display: flex;
  gap: ${SPACING.sm};
  justify-content: center;
  max-width: 300px;
  margin: 0 auto ${SPACING.lg};
`

// Format a number limit for display (0 = unlimited)
function renderValue(value: unknown): React.ReactNode {
  if (typeof value === 'boolean') return value ? <CheckOutlined /> : '—'
  if (typeof value === 'number') return value === 0 ? 'Unlimited' : value.toLocaleString()
  if (typeof value === 'string') return value
  return '—'
}

export function UpgradePage() {
  const { user } = useAuthContext()
  const { refetch } = useFeatureAccessContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [tierData, setTierData] = useState<TierComparisonData | null>(null)
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState(false)
  const [continuing, setContinuing] = useState(false)
  const [promoCode, setPromoCode] = useState('')

  const canceled = searchParams.get('canceled') === 'true'
  const isOnboarding = searchParams.get('onboarding') === 'true'

  useEffect(() => {
    analytics.upgradePageViewed({ context: isOnboarding ? 'onboarding' : 'direct' })
  }, [isOnboarding])

  useEffect(() => {
    ProfileService.getTierComparison()
      .then((response) => {
        if (response.data) {
          setTierData(response.data)
        }
      })
      .catch((err) => {
        console.error('Failed to fetch tier comparison:', err)
        Sentry.captureException(err, {
          tags: { feature: 'upgrade', action: 'fetch_tier_comparison' },
        })
      })
      .finally(() => setLoading(false))
  }, [])

  // Build comparison rows directly from DB labels — no hardcoded keys
  const comparisonRows = useMemo(() => {
    if (!tierData) return []
    const { free, paid } = tierData
    return Object.entries(free.labels ?? {}).map(([key, label]) => ({
      key,
      label,
      tooltip: free.tooltips?.[key],
      freeValue: (free as unknown as Record<string, unknown>)[key],
      paidValue: (paid as unknown as Record<string, unknown>)[key],
    }))
  }, [tierData])

  const handleUpgrade = async () => {
    if (!user) {
      void navigate('/login')
      return
    }

    setUpgrading(true)
    try {
      const response = await PaymentService.createCheckoutSession(promoCode || undefined)

      if (response.error) {
        if (response.error.code === 'ALREADY_SUBSCRIBED') {
          message.info('You already have an active subscription!')
          void navigate('/settings?section=billing')
          return
        }
        if (response.error.code === 'STRIPE_NOT_CONFIGURED') {
          message.error('Payment processing is not yet available. Please try again later.')
          return
        }
        message.error(response.error.message || 'Failed to start checkout')
        return
      }

      if (response.data?.url) {
        window.location.href = response.data.url
      }
    } catch (err) {
      console.error('Checkout error:', err)
      Sentry.captureException(err, {
        tags: { feature: 'upgrade', action: 'create_checkout' },
      })
      message.error('Failed to start checkout. Please try again.')
    } finally {
      setUpgrading(false)
    }
  }

  const handleContinueFree = async () => {
    if (isOnboarding) {
      setContinuing(true)
      try {
        const response = await ProfileService.markOnboardingComplete()
        if (response.error) {
          message.error('Something went wrong. Please try again.')
          return
        }
        await refetch()
        void navigate('/')
      } catch (err) {
        console.error('Continue free error:', err)
        Sentry.captureException(err, {
          tags: { feature: 'upgrade', action: 'continue_free' },
        })
        message.error('Something went wrong. Please try again.')
      } finally {
        setContinuing(false)
      }
    } else {
      void navigate(-1)
    }
  }

  if (loading) {
    return <SkeletonUpgrade />
  }

  return (
    <PageContainer>
      <Header>
        {isOnboarding ? (
          <>
            <WelcomeIcon>
              <RocketOutlined style={{ color: COLORS.primary }} />
            </WelcomeIcon>
            <Title>Welcome to Bandos!</Title>
            <Subtitle>
              Choose your plan to get started. You can always upgrade later from settings.
            </Subtitle>
          </>
        ) : (
          <>
            <Title>
              <CrownOutlined style={{ color: COLORS.primary }} />
              Upgrade to Pro
            </Title>
            <Subtitle>
              {tierData && tierData.paid.trial_days > 0 ? (
                <>
                  Start with a{' '}
                  <TrialHighlight>{tierData.paid.trial_days}-day free trial</TrialHighlight> — no
                  charge until it ends. Cancel anytime.
                </>
              ) : (
                'Unlock unlimited projects, exports, and team collaboration features.'
              )}
            </Subtitle>
          </>
        )}
      </Header>

      {canceled && (
        <Result
          status="info"
          title="Checkout Canceled"
          subTitle="No worries! Your checkout was canceled. You can try again when you're ready."
          style={{ marginBottom: SPACING.xl, padding: SPACING.lg }}
        />
      )}

      <ComparisonTable>
        <ComparisonHeader>
          <ComparisonHeaderCell>Feature</ComparisonHeaderCell>
          <ComparisonHeaderCell>Free</ComparisonHeaderCell>
          <ComparisonHeaderCell $highlight>Pro</ComparisonHeaderCell>
        </ComparisonHeader>

        <PriceRow>
          <PriceCell>
            <span
              style={{
                fontWeight: FONT_WEIGHT.semibold,
                color: 'var(--color-text-primary)',
                fontSize: TYPOGRAPHY.body.fontSize,
              }}
            >
              Price
            </span>
          </PriceCell>
          <PriceCell>
            <PriceAmount>${tierData?.free.price_monthly ?? 0}</PriceAmount>
            <PricePeriod>{tierData?.free.price_period ?? 'forever'}</PricePeriod>
          </PriceCell>
          <PriceCell $highlight>
            <PriceAmount>${tierData?.paid.price_monthly ?? 20}</PriceAmount>
            <PricePeriod>/ {tierData?.paid.price_period ?? 'month'}</PricePeriod>
          </PriceCell>
        </PriceRow>

        {comparisonRows.map(({ key, label, tooltip, freeValue, paidValue }) => (
          <ComparisonRow key={key}>
            <ComparisonCell>
              <FeatureWithTooltip>
                {label}
                {tooltip && <Tooltip content={tooltip} />}
              </FeatureWithTooltip>
            </ComparisonCell>
            <ComparisonCell>{renderValue(freeValue)}</ComparisonCell>
            <ComparisonCell $highlight>{renderValue(paidValue)}</ComparisonCell>
          </ComparisonRow>
        ))}
      </ComparisonTable>

      <CTASection>
        <PromoCodeSection>
          <Input
            placeholder="Promo code (optional)"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            style={{ maxWidth: 200 }}
            onPressEnter={handleUpgrade}
          />
        </PromoCodeSection>

        <UpgradeButton
          type="primary"
          size="large"
          icon={<ArrowRightOutlined />}
          loading={upgrading}
          onClick={handleUpgrade}
        >
          {tierData && tierData.paid.trial_days > 0
            ? `Start ${tierData.paid.trial_days}-day free trial`
            : `Upgrade for $${tierData?.paid.price_monthly ?? 20}/month`}
        </UpgradeButton>

        <ContinueFreeLink onClick={handleContinueFree} disabled={continuing}>
          {continuing ? 'Please wait...' : 'Continue with free plan'}
        </ContinueFreeLink>
      </CTASection>
    </PageContainer>
  )
}
